import React, { useState, useEffect } from 'react';
import { 
  FaUsers, 
  FaChartLine, 
  FaShieldAlt, 
  FaCog, 
  FaExclamationTriangle,
  FaCheckCircle,
  FaClock,
  FaEye,
  FaExchangeAlt,
  FaSignOutAlt,
  FaWallet,
  FaTrash,
  FaFileAlt,
  FaCalendarAlt,
  FaUser,
  FaUserTie,
  FaIdCard
} from 'react-icons/fa';
import { walletAPI } from '../../services/api';
import api from '../../services/api';
import { Link } from 'react-router-dom';
import WalletToWalletTransferModal from '../../components/WalletToWalletTransferModal';
import { useAuth } from '../../contexts/AuthContext';
import useInactivityTimer from '../../hooks/useInactivityTimer';
import InactivityTimer from '../../components/InactivityTimer';
import InactivityWarning from '../../components/InactivityWarning';
import RecentActivity from '../../components/RecentActivity';
import RecentLogins from '../../components/RecentLogins';
import axios from 'axios';

const WalletSummary = () => {
  const [filter, setFilter] = useState('all');
  const [totals, setTotals] = useState({ user: 0, distributor: 0 });

  useEffect(() => {
    const fetchTotals = async () => {
      if (filter === 'all') {
        const res = await walletAPI.getTotalBalances();
        setTotals({
          user: res.data.total_user_balance,
          distributor: res.data.total_distributor_balance,
        });
      } else {
        const res = await walletAPI.getTotalBalances(filter);
        setTotals({
          [filter]: res.data.total_balance,
        });
      }
    };
    fetchTotals();
  }, [filter]);

  return (
    <div className="mb-6 p-4 bg-white rounded-lg shadow-sm border border-gray-200 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
      <div className="flex items-center gap-2">
        <label className="font-semibold mr-2">Wallet Filter:</label>
        <select value={filter} onChange={e => setFilter(e.target.value)} className="border rounded px-2 py-1">
          <option value="all">All</option>
          <option value="user">User</option>
          <option value="distributor">Distributor</option>
        </select>
      </div>
      <div className="flex gap-6">
        {(filter === 'all' || filter === 'user') && (
          <div className="font-medium text-blue-700">Total in Users' Accounts: <span className="font-bold">₹{totals.user}</span></div>
        )}
        {(filter === 'all' || filter === 'distributor') && (
          <div className="font-medium text-green-700">Total in Distributors' Accounts: <span className="font-bold">₹{totals.distributor}</span></div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const { logout, user } = useAuth();
  const [stats] = useState({
    totalUsers: 1234,
    activeSessions: 456,
    revenue: 45678,
    systemStatus: 'Healthy'
  });
  const [adminBalance, setAdminBalance] = useState(0); // still used internally for transfers, not shown in UI
  const [paysprintMainBalance, setPaysprintMainBalance] = useState(null);
  const [paysprintCashBalance, setPaysprintCashBalance] = useState(null);
  const [showWalletToWalletModal, setShowWalletToWalletModal] = useState(false);
  const [showWalletStatusModal, setShowWalletStatusModal] = useState(false);
  const [walletStatusType, setWalletStatusType] = useState('fund'); // 'fund' or 'load'
  const [walletStatusRefId, setWalletStatusRefId] = useState('');
  const [walletStatusMerchant, setWalletStatusMerchant] = useState('PS00210301');
  const [walletStatusLoading, setWalletStatusLoading] = useState(false);
  const [walletStatusResult, setWalletStatusResult] = useState(null);
  const [showAgentRegistrationModal, setShowAgentRegistrationModal] = useState(false);
  const [agentForm, setAgentForm] = useState({
    merchant_code: 'PS00210301',
    name: '',
    mobile: '',
    email: '',
    pan_number: '',
    firmname: '',
    address: '',
    pincode: '',
    state: ''
  });
  const [agentSubmitting, setAgentSubmitting] = useState(false);
  const [agentError, setAgentError] = useState('');
  const [agentSuccess, setAgentSuccess] = useState('');

  // Inactivity timer
  const {
    timeLeft,
    showWarning,
    formatTime,
    handleContinue,
    handleLogout,
    setIsActive
  } = useInactivityTimer(logout, 240000); // 4 minutes

  // Expose fetchAdminBalance so it can be called after transfer
  const fetchAdminBalance = async () => {
    try {
      const response = await walletAPI.getUserWallet();
      setAdminBalance(response.data.balance || 0);
    } catch (error) {
      console.error('Error fetching admin balance:', error);
      setAdminBalance(0);
    }
  };

  // Fetch PaySprint partner main balance (credit balance)
  const fetchPaysprintMainBalance = async () => {
    try {
      // Laravel api.php routes are prefixed with /api
      const response = await api.get('/api/paysprint/main-balance');
      const wallet = response.data?.data?.wallet ?? response.data?.data?.raw?.wallet;
      setPaysprintMainBalance(wallet ?? null);
    } catch (error) {
      console.error('Error fetching PaySprint main balance:', error);
      setPaysprintMainBalance(null);
    }
  };


  const [userGrowth] = useState([
    { month: 'Jan', users: 1000 },
    { month: 'Feb', users: 1100 },
    { month: 'Mar', users: 1200 },
    { month: 'Apr', users: 1250 },
    { month: 'May', users: 1300 },
    { month: 'Jun', users: 1350 }
  ]);

  const [roleDistribution] = useState([
    { name: 'Admin', value: 5, color: '#ef4444' },
    { name: 'Distributor', value: 25, color: '#3b82f6' },
    { name: 'User', value: 1204, color: '#10b981' }
  ]);

  const [systemMetrics] = useState([
    { name: 'CPU Usage', value: 45, status: 'normal' },
    { name: 'Memory Usage', value: 78, status: 'warning' },
    { name: 'Disk Usage', value: 32, status: 'normal' },
    { name: 'Network', value: 12, status: 'normal' }
  ]);

  // Logo/Banner upload state
  const [settingsImages, setSettingsImages] = useState({ logo: '', banner: '' });
  const [logoFile, setLogoFile] = useState(null);
  const [bannerFile, setBannerFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [logoLoading, setLogoLoading] = useState(false);
  const [bannerLoading, setBannerLoading] = useState(false);
  const [currentTime, setCurrentTime] = useState(new Date());

  // Banner slider management state
  const [banners, setBanners] = useState([]);
  const [bannerUploadFile, setBannerUploadFile] = useState(null);
  const [bannerUploadMsg, setBannerUploadMsg] = useState('');
  const [bannerUploading, setBannerUploading] = useState(false);

  // Statement viewer state
  const [showStatementModal, setShowStatementModal] = useState(false);
  const [statementData, setStatementData] = useState([]);
  const [statementLoading, setStatementLoading] = useState(false);
  const [selectedUserType, setSelectedUserType] = useState('user'); // 'user' or 'distributor'
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedDateRange, setSelectedDateRange] = useState('monthly'); // 'monthly', 'weekly', 'custom'
  const [customStartDate, setCustomStartDate] = useState('');
  const [customEndDate, setCustomEndDate] = useState('');
  const [users, setUsers] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  // Open PaySprint Wallet Money portal via generate-url API
  const openWalletMoneyPortal = async () => {
    try {
      const refid = `WM${Date.now()}`;
      const redirectUrl = `${window.location.origin}/wallet-money/return`;

      // Backend route is /api/wallet-money/generate-url
      const response = await api.post('/api/wallet-money/generate-url', {
        merchant_code: 'PS00210301', // PaySprint Wallet Money merchant code
        refid,
        redirect_url: redirectUrl,
      });

      const { url, encdata } = response.data?.data || {};
      if (!url || !encdata) {
        alert('Unable to open Wallet Money portal. Please try again.');
        return;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = url;

      const textarea = document.createElement('textarea');
      textarea.name = 'encdata';
      textarea.style.display = 'none';
      textarea.value = encdata;
      form.appendChild(textarea);

      document.body.appendChild(form);
      form.submit();
      document.body.removeChild(form);
    } catch (error) {
      console.error('Error opening Wallet Money portal:', error);
      alert('Error opening Wallet Money portal. Please try again later.');
    }
  };

  // Check Wallet Money transaction status (fund transfer / wallet load)
  const fetchWalletMoneyStatus = async () => {
    if (!walletStatusRefId) {
      alert('Please enter Reference ID (refid/referenceid).');
      return;
    }
    setWalletStatusLoading(true);
    setWalletStatusResult(null);
    try {
      const endpoint =
        walletStatusType === 'fund'
          ? '/api/wallet-money/fund-transfer/status'
          : '/api/wallet-money/wallet-load/status';

      const payload =
        walletStatusType === 'fund'
          ? {
              referenceid: walletStatusRefId,
              merchant_code: walletStatusMerchant || undefined,
            }
          : {
              refid: walletStatusRefId,
              merchant_code: walletStatusMerchant || undefined,
            };

      const response = await api.post(endpoint, payload);
      setWalletStatusResult(response.data);
    } catch (error) {
      console.error('Error fetching Wallet Money status:', error);
      setWalletStatusResult({
        success: false,
        error: error.response?.data || error.message,
      });
    } finally {
      setWalletStatusLoading(false);
    }
  };

  // Fetch PaySprint partner cash balance (debit balance)
  const fetchPaysprintCashBalance = async () => {
    try {
      // Laravel api.php routes are prefixed with /api
      const response = await api.get('/api/paysprint/cash-balance');
      const wallet = response.data?.data?.wallet ?? response.data?.data?.raw?.cdwallet;
      setPaysprintCashBalance(wallet ?? null);
    } catch (error) {
      console.error('Error fetching PaySprint cash balance:', error);
      setPaysprintCashBalance(null);
    }
  };

  useEffect(() => {
    fetchAdminBalance();
    fetchPaysprintMainBalance();
    fetchPaysprintCashBalance();
    fetchSettingsImages();
    fetchBanners();
  }, []);

  // Update time every minute to refresh greeting
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 60000); // Update every minute
    return () => clearInterval(timer);
  }, []);

  const fetchSettingsImages = async () => {
    try {
      const res = await walletAPI.getSettingsImages();
      setSettingsImages(res.data);
    } catch (e) {
      console.error('Error fetching settings images:', e);
    }
  };

  const handleUpload = async (type) => {
    setUploading(true);
    setUploadMsg('');
    const file = type === 'logo' ? logoFile : bannerFile;
    if (!file) return;
    const formData = new FormData();
    formData.append('type', type);
    formData.append('image', file);
    try {
      const res = await walletAPI.uploadSettingsImage(formData);
      setUploadMsg(`${type.charAt(0).toUpperCase() + type.slice(1)} updated successfully!`);
      // Clear the file input
      if (type === 'logo') setLogoFile(null);
      if (type === 'banner') setBannerFile(null);
      // Refresh images after upload with a small delay
      setTimeout(async () => {
        await fetchSettingsImages();
      }, 500);
    } catch (e) {
      console.error(`${type} upload error:`, e);
      setUploadMsg('Upload failed: ' + (e.response?.data?.message || e.message));
    } finally {
      setUploading(false);
    }
  };

  const fetchBanners = async () => {
    try {
      const res = await walletAPI.get('/banners');
      setBanners(res.data.banners || []);
    } catch (e) {
      setBanners([]);
    }
  };

  const handleBannerUpload = async () => {
    if (!bannerUploadFile) {
      return;
    }
    setBannerUploading(true);
    setBannerUploadMsg('');
    const formData = new FormData();
    formData.append('image', bannerUploadFile);
    try {
      // Use axios directly for banner upload
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/admin/banners`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      setBannerUploadMsg('Banner uploaded successfully!');
      setBannerUploadFile(null);
      fetchBanners();
    } catch (e) {
      console.error('Banner upload error:', e, e?.response);
      let msg = 'Upload failed.';
      if (e?.response?.data?.error) msg = e.response.data.error;
      else if (e?.response?.data) msg = JSON.stringify(e.response.data);
      else if (e?.message) msg = e.message;
      setBannerUploadMsg(msg);
    } finally {
      setBannerUploading(false);
    }
  };

  const handleBannerDelete = async (id) => {
    if (!window.confirm('Delete this banner?')) return;
    try {
      await walletAPI.delete(`/admin/banners/${id}`);
      fetchBanners();
    } catch (e) {}
  };

  // Statement viewer functions
  const fetchUsers = async () => {
    try {
      const response = await api.get('/api/admin/users?role=user');
      // Handle paginated response - data is in response.data.data.data
      const usersData = response.data?.data?.data || response.data?.data || response.data?.users || [];
      setUsers(Array.isArray(usersData) ? usersData : []);
    } catch (error) {
      console.error('Error fetching users:', error);
      setUsers([]);
    }
  };

  const fetchDistributors = async () => {
    try {
      const response = await api.get('/api/admin/users?role=distributor');
      // Handle paginated response - data is in response.data.data.data
      const distributorsData = response.data?.data?.data || response.data?.data || response.data?.users || [];
      setDistributors(Array.isArray(distributorsData) ? distributorsData : []);
    } catch (error) {
      console.error('Error fetching distributors:', error);
      setDistributors([]);
    }
  };

  const fetchStatement = async () => {
    if (!selectedUserId) {
      alert('Please select a user or distributor');
      return;
    }

    setStatementLoading(true);
    try {
      let startDate, endDate;
      const now = new Date();

      switch (selectedDateRange) {
        case 'monthly':
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
          break;
        case 'weekly':
          const startOfWeek = new Date(now);
          startOfWeek.setDate(now.getDate() - now.getDay());
          startDate = startOfWeek;
          endDate = new Date(startOfWeek);
          endDate.setDate(startOfWeek.getDate() + 6);
          break;
        case 'custom':
          if (!customStartDate || !customEndDate) {
            alert('Please select custom date range');
            return;
          }
          startDate = new Date(customStartDate);
          endDate = new Date(customEndDate);
          break;
        default:
          startDate = new Date(now.getFullYear(), now.getMonth(), 1);
          endDate = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      }

      const params = {
        user_id: selectedUserId,
        start_date: startDate.toISOString().split('T')[0],
        end_date: endDate.toISOString().split('T')[0],
        user_type: selectedUserType
      };

      const response = await api.post('/api/admin/statement/generate', params);
      setStatementData(response.data.transactions || []);
    } catch (error) {
      console.error('Error fetching statement:', error);
      alert('Error fetching statement: ' + (error.response?.data?.message || error.message));
      setStatementData([]);
    } finally {
      setStatementLoading(false);
    }
  };

  const openStatementModal = () => {
    setShowStatementModal(true);
    fetchUsers();
    fetchDistributors();
  };

  const closeStatementModal = () => {
    setShowStatementModal(false);
    setStatementData([]);
    setSelectedUserId('');
    setSelectedUserType('user');
    setSelectedDateRange('monthly');
    setCustomStartDate('');
    setCustomEndDate('');
  };


  const actionTiles = [
    { label: 'Wallet To Wallet', icon: <FaExchangeAlt className="mx-auto text-orange-600 text-2xl" /> },
    { label: 'Statement Viewer', icon: <FaFileAlt className="mx-auto text-blue-600 text-2xl" /> },
    { label: 'Wallet Agent Registration', icon: <FaUserTie className="mx-auto text-green-600 text-2xl" /> },
    { label: 'Wallet Money Portal', icon: <FaWallet className="mx-auto text-purple-600 text-2xl" /> },
    { label: 'Wallet Txn Status', icon: <FaWallet className="mx-auto text-indigo-600 text-2xl" /> },
  ];

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://127.0.0.1:8000';
  // Helper to get full image URL with cache busting
  const getImageUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    const baseUrl = `${API_BASE_URL}${url}`;
    // Add cache busting parameter to prevent caching issues
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}t=${Date.now()}`;
  };

  // Helper function to get logo URL with fallback
  const getLogoUrl = () => {
    // For now, always use the fallback logo to prevent flickering
    return '/bt-india-logo.jpeg';
  };

    // Helper function to get time-based greeting
  const getTimeBasedGreeting = () => {
    const hour = currentTime.getHours();

    if (hour < 12) {
      return 'Good Morning';
    } else if (hour < 17) {
      return 'Good Afternoon';
    } else {
      return 'Good Evening';
    }
  };

  return (
    <div className="min-h-screen h-screen flex flex-col bg-gray-100">
      {/* Header */}
      <header className="bg-gradient-to-r from-indigo-900 via-blue-900 to-blue-700 text-white px-4 sm:px-6 py-3">
        {/* Mobile Header */}
        <div className="flex items-center justify-between lg:hidden">
          <div className="flex items-center gap-2">
            <img src={getLogoUrl()} alt="BT India Logo" className="h-8 w-auto" />
            <h1 className="text-lg font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={logout} className="px-3 py-1 bg-blue-800 hover:bg-blue-900 rounded text-white text-sm">Logout</button>
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src={getLogoUrl()} alt="BT India Logo" className="h-16 w-auto" />
            <div>
              <h1 className="text-2xl font-bold tracking-wide">Admin Dashboard</h1>
            </div>
            <InactivityTimer 
              timeLeft={timeLeft} 
              formatTime={formatTime} 
              isActive={!!user} 
            />
          </div>
          <div className="flex-1 flex justify-center space-x-8 text-xs md:text-base">
            <div className="flex flex-col items-center">
              <span className="text-blue-200">PaySprint Main Balance</span>
              <span className="text-yellow-300">
                {paysprintMainBalance !== null ? `₹${paysprintMainBalance}` : '—'}
              </span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-blue-200">PaySprint Cash Balance</span>
              <span className="text-pink-300">
                {paysprintCashBalance !== null ? `₹${paysprintCashBalance}` : '—'}
              </span>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <span className="font-semibold">{user?.email || user?.username || 'Admin'}</span>
            <button
              onClick={logout}
              className="ml-4 px-4 py-2 bg-blue-800 hover:bg-blue-900 rounded text-white font-semibold text-sm shadow flex items-center"
            >
              <FaSignOutAlt className="mr-2" />
              Logout
            </button>
          </div>
        </div>

        {/* Mobile Balance Bar */}
        <div className="lg:hidden mt-3 bg-blue-800 rounded-lg p-3">
          <div className="flex justify-around">
            <div className="flex flex-col items-center">
              <span className="text-blue-200 text-sm">Main Balance</span>
              <span className="text-yellow-300 font-bold text-lg">
                {paysprintMainBalance !== null ? `₹${paysprintMainBalance}` : '—'}
              </span>
            </div>
            <div className="flex flex-col items-center">
              <span className="text-blue-200 text-sm">Cash Balance</span>
              <span className="text-pink-300 font-bold text-lg">
                {paysprintCashBalance !== null ? `₹${paysprintCashBalance}` : '—'}
              </span>
            </div>
          </div>
        </div>
      </header>
      <div className="flex flex-1 min-h-0">
        {/* Mobile Menu Button */}
        <button 
          className="lg:hidden fixed top-4 left-4 z-50 bg-blue-900 text-white p-2 rounded-lg shadow-lg"
          onClick={() => setShowMobileMenu(!showMobileMenu)}
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Mobile Menu Overlay */}
        {showMobileMenu && (
          <div 
            className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-40"
            onClick={() => setShowMobileMenu(false)}
          />
        )}

        {/* Sidebar */}
        <aside className={`w-64 bg-gradient-to-b from-indigo-900 to-blue-900 text-white flex flex-col justify-between p-4 min-h-0 fixed lg:relative inset-y-0 left-0 z-50 transform transition-transform duration-300 ease-in-out ${showMobileMenu ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
          {/* Close button for mobile */}
          <button
            onClick={() => setShowMobileMenu(false)}
            className="lg:hidden absolute top-4 right-4 p-2 text-white hover:bg-blue-800 rounded"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <div>
            <div className="mb-4">
              <div className="uppercase text-xs text-blue-200 font-bold mb-2">System Management</div>
              <ul className="space-y-1 text-sm">
                <Link to="/admin/users" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaUsers className="mr-2" />
                  <span>User Management</span>
                </Link>
                <Link to="/admin/wallets" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaWallet className="mr-2" />
                  <span>Wallet Management</span>
                </Link>
                <Link to="/admin/payment-requests" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <span>Payment Requests</span>
                </Link>
                <Link to="/admin/kyc" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaIdCard className="mr-2" />
                  <span>KYC Management</span>
                </Link>
                <Link to="/admin/analytics" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaChartLine className="mr-2" />
                  <span>Analytics</span>
                </Link>
                <Link to="/admin/security" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaShieldAlt className="mr-2" />
                  <span>Security</span>
                </Link>
                <Link to="/admin/settings" className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaCog className="mr-2" />
                  <span>Settings</span>
                </Link>
              </ul>
            </div>
            <div className="mb-4">
              <div className="uppercase text-xs text-blue-200 font-bold mb-2">Quick Actions</div>
              <ul className="space-y-1 text-sm">
                <li
                  className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center"
                  onClick={() => setShowWalletToWalletModal(true)}
                >
                  <FaExchangeAlt className="mr-2" />
                  <span>Wallet Transfer</span>
                </li>
                <li
                  className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center"
                  onClick={openStatementModal}
                >
                  <FaFileAlt className="mr-2" />
                  <span>View Statements</span>
                </li>
                <li className="p-2 rounded hover:bg-blue-800 cursor-pointer flex items-center">
                  <FaEye className="mr-2" />
                  <span>System Status</span>
                </li>
              </ul>
            </div>
            <div className="mt-8 text-center">
              <div className="text-pink-400 font-bold text-lg mb-2">SYSTEM STATUS</div>
              <div className="bg-blue-800 rounded-lg p-3 mb-2">
                <div className="flex items-center justify-center">
                  <FaCheckCircle className="text-green-400 text-2xl mr-2" />
                  <div>
                    <div className="font-semibold">All Systems Operational</div>
                    <div className="text-xs text-green-400">Healthy</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <footer className="text-center text-xs text-blue-200 mt-8">© {new Date().getFullYear()} BT India. All rights reserved.</footer>
        </aside>
        {/* Main Content */}
        <main className="flex-1 p-2 sm:p-4 min-h-0 overflow-auto bg-gray-50 lg:ml-0">
          {/* Greeting */}
          <div className="text-xl font-semibold text-blue-900 mb-4">Hey ! {getTimeBasedGreeting()} , Hope You are doing Good !!!</div>
          
          {/* Logo/Banner Update Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Update Logo & Banner</h2>
              <div className="flex items-center space-x-4">
                <div className="flex space-x-2">
                  <button 
                    onClick={fetchSettingsImages}
                    className="px-3 py-1 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded text-sm font-medium transition-colors"
                  >
                    Refresh
                  </button>
                  <button 
                    onClick={() => {
                      if (settingsImages.logo) {
                        window.open(getImageUrl(settingsImages.logo), '_blank');
                      }
                    }}
                    className="px-3 py-1 bg-blue-100 hover:bg-blue-200 text-blue-700 rounded text-sm font-medium transition-colors"
                    disabled={!settingsImages.logo}
                  >
                    Test Logo
                  </button>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-sm text-gray-600">Media Management</span>
                </div>
              </div>
            </div>
            
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Logo Section */}
              <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Logo</h3>
                {settingsImages.logo ? (
                    <div className="mb-4">
                      <div className="relative">
                        <img 
                          src={getImageUrl(settingsImages.logo)} 
                          alt="Current Logo" 
                          className="mx-auto h-20 w-auto rounded-lg shadow-md border-2 border-white max-w-full" 
                          onLoadStart={() => setLogoLoading(true)}
                          onError={(e) => {
                            setLogoLoading(false);
                            e.target.style.display = 'none';
                            // Show fallback
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            setLogoLoading(false);
                            // Hide fallback on successful load
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'none';
                          }}
                        />
                        <div className="h-20 w-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center" style={{display: logoLoading ? 'flex' : 'none'}}>
                          <span className="text-gray-400 text-sm">{logoLoading ? 'Loading...' : 'Failed to load'}</span>
                        </div>
                      </div>
                      <p className="text-sm text-green-600 mt-2">✓ Logo uploaded successfully</p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="h-20 w-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No logo set</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Upload a logo image</p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div className="relative">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={e => setLogoFile(e.target.files[0])}
                        className="hidden"
                        id="logo-upload"
                      />
                      <label 
                        htmlFor="logo-upload"
                        className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors text-center font-medium"
                      >
                        Choose Logo File
                      </label>
                    </div>
                    <button 
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium" 
                      disabled={uploading || !logoFile} 
                      onClick={() => handleUpload('logo')}
                    >
                      {uploading ? 'Uploading...' : 'Upload Logo'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Banner Section */}
              <div className="bg-gray-50 rounded-lg p-6 border-2 border-dashed border-gray-300">
                <div className="text-center">
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Current Banner</h3>
                {settingsImages.banner ? (
                    <div className="mb-4">
                      <div className="relative">
                        <img 
                          src={getImageUrl(settingsImages.banner)} 
                          alt="Current Banner" 
                          className="mx-auto h-20 w-auto rounded-lg shadow-md border-2 border-white max-w-full" 
                          onLoadStart={() => setBannerLoading(true)}
                          onError={(e) => {
                            setBannerLoading(false);
                            e.target.style.display = 'none';
                            // Show fallback
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'flex';
                          }}
                          onLoad={(e) => {
                            setBannerLoading(false);
                            // Hide fallback on successful load
                            const fallback = e.target.nextElementSibling;
                            if (fallback) fallback.style.display = 'none';
                          }}
                        />
                        <div className="h-20 w-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center" style={{display: bannerLoading ? 'flex' : 'none'}}>
                          <span className="text-gray-400 text-sm">{bannerLoading ? 'Loading...' : 'Failed to load'}</span>
                        </div>
                      </div>
                      <p className="text-sm text-green-600 mt-2">✓ Banner uploaded successfully</p>
                    </div>
                  ) : (
                    <div className="mb-4">
                      <div className="h-20 w-32 mx-auto bg-gray-200 rounded-lg flex items-center justify-center">
                        <span className="text-gray-400 text-sm">No banner set</span>
                      </div>
                      <p className="text-sm text-gray-500 mt-2">Upload a banner image</p>
                    </div>
                  )}
                  
                  <div className="space-y-3">
                    <div className="relative">
                      <input 
                        type="file" 
                        accept="image/*" 
                        onChange={e => setBannerFile(e.target.files[0])}
                        className="hidden"
                        id="banner-upload"
                      />
                      <label 
                        htmlFor="banner-upload"
                        className="block w-full px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors text-center font-medium"
                      >
                        Choose Banner File
                      </label>
                    </div>
                    <button 
                      className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors font-medium" 
                      disabled={uploading || !bannerFile} 
                      onClick={() => handleUpload('banner')}
                    >
                      {uploading ? 'Uploading...' : 'Upload Banner'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {uploadMsg && (
              <div className="mt-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <FaCheckCircle className="text-green-600 mr-2" />
                  <span className="text-green-800 font-semibold">{uploadMsg}</span>
                </div>
              </div>
            )}
          </div>
          {/* Slider Banner Management Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-gray-900">Slider Banner Management</h2>
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600">Slider Images ({banners.length}/3)</span>
              </div>
            </div>
            
            {/* Current Banners Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {(banners || []).map((banner, index) => (
                <div key={banner.id} className="relative group bg-gray-50 rounded-lg p-4 border-2 border-gray-200 hover:border-blue-300 transition-colors">
                  <div className="relative">
                    <img 
                      src={banner.image_url} 
                      alt={`Slider Banner ${index + 1}`} 
                      className="w-full h-32 object-cover rounded-lg shadow-md" 
                    />
                    <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button 
                        onClick={() => handleBannerDelete(banner.id)} 
                        className="bg-red-600 text-white rounded-full p-2 hover:bg-red-700 shadow-lg transition-colors"
                        title="Delete Banner"
                      >
                        <FaTrash className="text-sm" />
                      </button>
                    </div>
                    <div className="absolute bottom-2 left-2 bg-black bg-opacity-50 text-white px-2 py-1 rounded text-xs">
                      Banner {index + 1}
                    </div>
                  </div>
                </div>
              ))}
              
              {/* Add New Banner Card */}
              {banners.length < 3 && (
                <div className="bg-gray-50 rounded-lg p-4 border-2 border-dashed border-gray-300 hover:border-blue-400 transition-colors">
                  <div className="text-center h-32 flex flex-col items-center justify-center">
                    <p className="text-sm text-gray-600 mb-3">Add New Banner</p>
                    <div className="space-y-2">
                      <div className="relative">
                        <input 
                          type="file" 
                          accept="image/*" 
                          onChange={e => setBannerUploadFile(e.target.files[0])}
                          className="hidden"
                          id="banner-upload-new"
                        />
                        <label 
                          htmlFor="banner-upload-new"
                          className="block px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 cursor-pointer transition-colors text-sm font-medium"
                        >
                          Choose Image
                        </label>
                      </div>
                      <button 
                        className="w-full px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors text-sm font-medium" 
                        disabled={bannerUploading || !bannerUploadFile} 
                        onClick={handleBannerUpload}
                      >
                        {bannerUploading ? 'Uploading...' : 'Upload Banner'}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
            
            {/* Status Messages */}
            {bannerUploadMsg && (
              <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center">
                  <FaCheckCircle className="text-green-600 mr-2" />
                  <span className="text-green-800 font-semibold">{bannerUploadMsg}</span>
                </div>
              </div>
            )}
            
            {/* Banner Limit Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center">
                <div>
                  <p className="text-blue-800 font-medium">
                    {banners.length === 3 ? 'Maximum banners reached' : `${3 - banners.length} banner${3 - banners.length === 1 ? '' : 's'} remaining`}
                  </p>
                  <p className="text-blue-600 text-sm">
                    {banners.length === 3 
                      ? 'Delete a banner to add a new one.' 
                      : 'Upload up to 3 banner images for the slider.'}
                  </p>
                </div>
              </div>
            </div>
          </div>
          {/* Wallet Summary */}
          <WalletSummary />
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
              <p className="text-gray-600">System overview and management</p>
            </div>
            <div className="flex space-x-3">
              <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                Generate Report
              </button>
              <button className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors">
                Export Data
              </button>
            </div>
          </div>

            {/* Action Tiles */}
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-2 sm:gap-4 mb-6">
            {actionTiles.map((tile, idx) => (
              <div
                key={idx}
                className="bg-white rounded-lg shadow p-4 flex flex-col items-center hover:bg-blue-50 cursor-pointer transition"
                onClick={
                  tile.label === 'Wallet To Wallet'
                    ? () => setShowWalletToWalletModal(true)
                    : tile.label === 'Statement Viewer'
                    ? openStatementModal
                    : tile.label === 'Wallet Agent Registration'
                    ? () => {
                        setAgentError('');
                        setAgentSuccess('');
                        setShowAgentRegistrationModal(true);
                      }
                    : tile.label === 'Wallet Money Portal'
                    ? openWalletMoneyPortal
                    : tile.label === 'Wallet Txn Status'
                    ? () => setShowWalletStatusModal(true)
                    : undefined
                }
              >
                {tile.icon}
                <span className="mt-2 text-xs text-center font-medium text-gray-700">{tile.label}</span>
              </div>
            ))}
          </div>

          {/* Recent Activity Section */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <RecentActivity limit={10} showTitle={true} />
            <RecentLogins limit={3} showTitle={true} />
          </div>
        </main>
      </div>
      
      {/* Modals */}
      <WalletToWalletTransferModal 
        show={showWalletToWalletModal} 
        onHide={() => setShowWalletToWalletModal(false)} 
        onSuccess={null}
        onBalanceUpdate={fetchAdminBalance}
      />

      {/* Wallet Agent Registration Modal */}
      {showAgentRegistrationModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Wallet Agent Registration</h2>
              <button
                onClick={() => {
                  setShowAgentRegistrationModal(false);
                  setAgentError('');
                  setAgentSuccess('');
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              {agentError && (
                <div className="p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
                  {agentError}
                </div>
              )}
              {agentSuccess && (
                <div className="p-3 bg-green-50 border border-green-200 rounded text-sm text-green-700">
                  {agentSuccess}
                </div>
              )}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Merchant Code</label>
                  <input
                    type="text"
                    value={agentForm.merchant_code}
                    onChange={(e) => setAgentForm({ ...agentForm, merchant_code: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="PS00210301"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Agent Name</label>
                  <input
                    type="text"
                    value={agentForm.name}
                    onChange={(e) => setAgentForm({ ...agentForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Full name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input
                    type="tel"
                    value={agentForm.mobile}
                    onChange={(e) => setAgentForm({ ...agentForm, mobile: e.target.value.replace(/[^0-9]/g, '').slice(0, 10) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="10-digit mobile"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={agentForm.email}
                    onChange={(e) => setAgentForm({ ...agentForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="email@example.com"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number</label>
                  <input
                    type="text"
                    value={agentForm.pan_number}
                    onChange={(e) =>
                      setAgentForm({
                        ...agentForm,
                        pan_number: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 10),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="ABCDE1234F"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Pincode</label>
                  <input
                    type="text"
                    value={agentForm.pincode}
                    onChange={(e) =>
                      setAgentForm({
                        ...agentForm,
                        pincode: e.target.value.replace(/[^0-9]/g, '').slice(0, 6),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="6-digit pincode"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Firm Name</label>
                  <input
                    type="text"
                    value={agentForm.firmname}
                    onChange={(e) =>
                      setAgentForm({
                        ...agentForm,
                        firmname: e.target.value.replace(/[^a-zA-Z0-9\s,.-]/g, ''),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Firm name (no special chars)"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address</label>
                  <input
                    type="text"
                    value={agentForm.address}
                    onChange={(e) =>
                      setAgentForm({
                        ...agentForm,
                        address: e.target.value.replace(/[^a-zA-Z0-9\s,.-]/g, ''),
                      })
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="Address (no special chars)"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">State</label>
                  <input
                    type="text"
                    value={agentForm.state}
                    onChange={(e) => setAgentForm({ ...agentForm, state: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="State"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 mt-4">
                <button
                  onClick={() => {
                    setShowAgentRegistrationModal(false);
                    setAgentError('');
                    setAgentSuccess('');
                  }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={agentSubmitting}
                >
                  Cancel
                </button>
                <button
                  onClick={async () => {
                    setAgentError('');
                    setAgentSuccess('');
                    if (!agentForm.merchant_code || !agentForm.name || !agentForm.mobile || !agentForm.email || !agentForm.pan_number || !agentForm.firmname || !agentForm.address || !agentForm.pincode || !agentForm.state) {
                      setAgentError('All fields are required.');
                      return;
                    }
                    if (agentForm.mobile.length !== 10) {
                      setAgentError('Mobile must be 10 digits.');
                      return;
                    }
                    if (agentForm.pincode.length !== 6) {
                      setAgentError('Pincode must be 6 digits.');
                      return;
                    }
                    if (agentForm.pan_number.length !== 10) {
                      setAgentError('PAN must be 10 characters.');
                      return;
                    }
                    try {
                      setAgentSubmitting(true);
                      const payload = {
                        merchant_code: agentForm.merchant_code,
                        name: agentForm.name,
                        mobile: agentForm.mobile,
                        email: agentForm.email,
                        pan_number: agentForm.pan_number,
                        firmname: agentForm.firmname,
                        address: agentForm.address,
                        pincode: agentForm.pincode,
                        state: agentForm.state,
                      };
                      const response = await api.post('/api/wallet-money/agent/registration', payload);
                      if (response.data?.success) {
                        setAgentSuccess(response.data.message || 'Agent registered successfully.');
                      } else {
                        setAgentError(response.data?.message || 'Agent registration failed.');
                      }
                    } catch (error) {
                      const apiMessage =
                        error.response?.data?.message ||
                        (error.response?.data?.errors ? 'Validation failed on server.' : error.message);
                      setAgentError(apiMessage);
                    } finally {
                      setAgentSubmitting(false);
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                  disabled={agentSubmitting}
                >
                  {agentSubmitting ? 'Registering...' : 'Register Agent'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Wallet Money Transaction Status Modal */}
      {showWalletStatusModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Wallet Money Transaction Status</h2>
              <button
                onClick={() => {
                  setShowWalletStatusModal(false);
                  setWalletStatusResult(null);
                }}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status Type
                  </label>
                  <select
                    value={walletStatusType}
                    onChange={(e) => setWalletStatusType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="fund">Fund Transfer Status</option>
                    <option value="load">Wallet Load Status</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Merchant Code
                  </label>
                  <input
                    type="text"
                    value={walletStatusMerchant}
                    onChange={(e) => setWalletStatusMerchant(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="PS002103"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reference ID (refid / referenceid)
                </label>
                <input
                  type="text"
                  value={walletStatusRefId}
                  onChange={(e) => setWalletStatusRefId(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Enter transaction refid"
                />
              </div>
              <div className="flex justify-between items-center">
                <button
                  onClick={fetchWalletMoneyStatus}
                  disabled={walletStatusLoading || !walletStatusRefId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {walletStatusLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                      Checking...
                    </>
                  ) : (
                    'Check Status'
                  )}
                </button>
              </div>
              <div className="mt-2 max-h-64 overflow-auto bg-gray-50 border border-gray-200 rounded p-3 text-xs font-mono text-gray-800">
                {walletStatusResult ? (
                  <pre>{JSON.stringify(walletStatusResult, null, 2)}</pre>
                ) : (
                  <span className="text-gray-400">
                    Result will appear here after you check status.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

       {/* Statement Viewer Modal */}
       {showStatementModal && (
         <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
           <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-6 border-b border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 flex items-center">
                <FaFileAlt className="mr-3 text-blue-600" />
                Statement Viewer
              </h2>
              <button
                onClick={closeStatementModal}
                className="text-gray-400 hover:text-gray-600 text-2xl"
              >
                ×
              </button>
            </div>
            
             <div className="p-4 sm:p-6">
               {/* Filters Section */}
               <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                {/* User Type Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    User Type
                  </label>
                  <select
                    value={selectedUserType}
                    onChange={(e) => {
                      setSelectedUserType(e.target.value);
                      setSelectedUserId('');
                    }}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="user">User</option>
                    <option value="distributor">Distributor</option>
                  </select>
                </div>

                {/* User/Distributor Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {selectedUserType === 'user' ? 'Select User' : 'Select Distributor'}
                  </label>
                  <select
                    value={selectedUserId}
                    onChange={(e) => setSelectedUserId(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">Choose {selectedUserType}...</option>
                    {(() => {
                      const currentList = selectedUserType === 'user' ? users : distributors;
                      const safeList = Array.isArray(currentList) ? currentList : [];
                      return safeList.map((item) => (
                        <option key={item.id} value={item.id}>
                          {item.name} ({item.email})
                        </option>
                      ));
                    })()}
                  </select>
                </div>

                {/* Date Range Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Date Range
                  </label>
                  <select
                    value={selectedDateRange}
                    onChange={(e) => setSelectedDateRange(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="monthly">This Month</option>
                    <option value="weekly">This Week</option>
                    <option value="custom">Custom Range</option>
                  </select>
                </div>

                {/* Custom Date Range */}
                {selectedDateRange === 'custom' && (
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        Start Date
                      </label>
                      <input
                        type="date"
                        value={customStartDate}
                        onChange={(e) => setCustomStartDate(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-700 mb-1">
                        End Date
                      </label>
                      <input
                        type="date"
                        value={customEndDate}
                        onChange={(e) => setCustomEndDate(e.target.value)}
                        className="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="flex justify-between items-center mb-6">
                <button
                  onClick={fetchStatement}
                  disabled={statementLoading || !selectedUserId}
                  className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center"
                >
                  {statementLoading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Loading...
                    </>
                  ) : (
                    <>
                      <FaCalendarAlt className="mr-2" />
                      Generate Statement
                    </>
                  )}
                </button>
                
                {statementData.length > 0 && (
                  <div className="text-sm text-gray-600">
                    Found {statementData.length} transactions
                  </div>
                )}
              </div>

              {/* Statement Data Table */}
              {statementData.length > 0 ? (
                <div className="overflow-x-auto -mx-4 sm:mx-0">
                  <table className="min-w-full bg-white border border-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Amount
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Balance
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Status
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {(statementData || []).map((transaction, index) => (
                        <tr key={index} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {new Date(transaction.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              transaction.type === 'credit' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.type}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            {transaction.description || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <span className={transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'}>
                              {transaction.type === 'credit' ? '+' : '-'}₹{Math.abs(transaction.amount).toLocaleString()}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            ₹{transaction.balance?.toLocaleString() || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              transaction.status === 'completed' 
                                ? 'bg-green-100 text-green-800' 
                                : transaction.status === 'pending'
                                ? 'bg-yellow-100 text-yellow-800'
                                : 'bg-red-100 text-red-800'
                            }`}>
                              {transaction.status}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : statementLoading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="mt-2 text-gray-600">Loading statement...</p>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <FaFileAlt className="mx-auto text-4xl text-gray-300 mb-4" />
                  <p>No statement data available. Please select a user/distributor and generate statement.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Inactivity Warning Popup */}
      <InactivityWarning 
        showWarning={showWarning}
        handleContinue={handleContinue}
        handleLogout={handleLogout}
      />
    </div>
  );
};

export default AdminDashboard; 